import { createSelector, createSlice } from '@reduxjs/toolkit';
import { keyBy } from 'lodash';
import { addItemsToList, sar } from '../../../helpers/reduxHelper';
import { selectAllLinkedObjects } from './configuratorSceneLabels.slicer';

const getInitialState = () => ({
  selectedObjects: [],
  selectedBackupObjects: [],
  propTree: [],
  isTransformationPanelMinimize: true,
});

const configuratorUnityObject = createSlice({
  name: 'configuratorUnityObject',
  initialState: getInitialState(),
  reducers: {
    resetState: () => {
      return getInitialState();
    },
    setSelectedObjects: sar('selectedObjects'),
    setSelectedBackupObjects: sar('selectedBackupObjects'),
    setPropTree: sar('propTree'),
    addPropToTree: addItemsToList('propTree'),
    updateObjectTransform: (state, { payload }) => {
      const { id, transformData } = payload;
      state.selectedObjects = state.selectedObjects.map((o) => {
        return o.id !== id
          ? o
          : {
              ...o,
              newTransformData: {
                ...o.newTransformData,
                ...transformData,
              },
            };
      });
    },
    // the transformation panel treats a whole prop group as one object: it reads and writes groupTransformData,
    // which is the transform of the group's parent object in unity, and unity puts a copy of it on every prop of the
    // group. So an optimistic update for a group has to land on every selected prop of that group.
    updateGroupTransform: (state, { payload }) => {
      const { groupObjectName, transformData } = payload;
      state.selectedObjects = state.selectedObjects.map((o) => {
        return o.groupObjectName !== groupObjectName
          ? o
          : {
              ...o,
              groupTransformData: {
                ...o.groupTransformData,
                ...transformData,
              },
            };
      });
    },
    syncObjectTransform: (state, { payload }) => {
      const { transformData } = payload;
      const map = keyBy(transformData, (o) => o.ObjectName);
      state.selectedObjects = state.selectedObjects.map((o) => {
        const newTransformData = map[o.objectName];
        // The transform of the parent object of a group is what the transformation panel shows for a whole group, so
        // it has to be taken from the payload as well. Unity reports it alongside the props whenever it moves the
        // group itself, which is what a reset of a group does. Without it the panel keeps showing the values from
        // before the reset until the selection changes.
        const newGroupTransformData = o.groupObjectName ? map[o.groupObjectName] : undefined;
        if (!newTransformData && !newGroupTransformData) {
          // Unity reports only the objects it moved, so an object of the selection which is not in the payload was
          // not moved and keeps the transform it already has.
          return o;
        }
        return {
          ...o,
          ...(newTransformData ? { newTransformData } : {}),
          ...(newGroupTransformData ? { groupTransformData: newGroupTransformData } : {}),
        };
      });
    },
    toggleTransformationPanel: (state, { payload }) => {
      state.isTransformationPanelMinimize = payload;
    },
  },
});

export const configuratorUnityObjectActions = configuratorUnityObject.actions;

export const configuratorUnityObjectSelector = (state) => state.configuratorUnityObject;

export const configuratorUnityObjectReducer = configuratorUnityObject.reducer;

export default configuratorUnityObjectReducer;

export const selectSelectedBackupObjects = (state) =>
  state.configuratorUnityObject.selectedBackupObjects;

export const selectSelectedObjects = (state) => state.configuratorUnityObject.selectedObjects;

export const selectHasOneObjectSelected = (state) =>
  state.configuratorUnityObject.selectedObjects.length === 1;

export const selectHasSelectedObject = (state) =>
  state.configuratorUnityObject.selectedObjects.length > 0;

export const selectCanApplyMTC = (state) =>
  state.configuratorUnityObject.selectedObjects.length > 0;

export const selectMultipleObjectsSelected = (state) =>
  state.configuratorUnityObject.selectedObjects.length > 1;

export const selectPropTree = (state) => state.configuratorUnityObject.propTree;

export const selectPropFlatList = (state) => {
  const tree = selectPropTree(state);
  return tree;
};

export const selectIsTransformPanelMinimize = (state) =>
  state.configuratorUnityObject.isTransformationPanelMinimize;

export const selectCurrentSceneUnlinkedObjects = createSelector(
  (state) => selectAllLinkedObjects(state),
  (state) => selectPropFlatList(state),
  (linkedObjects, allObjects) => {
    const unlinkedObjects = allObjects.filter(
      (a) => !linkedObjects.map((b) => b.objectName).includes(a.objectName)
    );
    return unlinkedObjects;
  }
);
